import {Customer} from './customers.types.js'

const defaultCustomerConfirmedEmailSubject = 'Zending aangemeld'
const defaultCustomerConfirmedEmailBody = `Beste {naam},

Je zending is aangemeld voor verzending bij {vervoerder}. Klik op onderstaande knop om je zending te volgen.

Met vriendelijke groet,
{bedrijfsnaam}`

const defaultCustomerShippedEmailSubject = 'Zending verzonden'
const defaultCustomerShippedEmailBody = `Beste {naam},

Je zending is verzonden met {vervoerder}. Klik op onderstaande knop om je zending te volgen.

Met vriendelijke groet,
{bedrijfsnaam}`

const defaultCustomerCancelEmailSubject = 'Zending geannuleerd'
const defaultCustomerCancelEmailBody = `Beste {naam},

Wij hebben de zending {barcode} geannuleerd.
Helaas kunt u deze zending niet meer verwachten.

Met vriendelijke groet,
{bedrijfsnaam}`

const defaultCustomerRetourConfirmationEmailSubject = 'Retourlabel'
const defaultCustomerRetourConfirmationEmailBody = `Beste {naam},

Bedankt voor het aanmelden van je retourzending.
Het verzendlabel vind je in de bijlage.

Print het label, plak deze op het pakket en lever deze in bij een inleverpunt van {vervoerder}. De zending is vervolgens te volgen via de Track & Trace link onderaan deze email.

Met vriendelijke groet,

{bedrijfsnaam}`

export default (): Customer => {
    const customer: Customer = {
        name: '',
        synced: false,
        debtorCode: '',
        useOtherInvoiceDebtorCode: false,
        invoiceDebtorCode: '',
        invoiceForCustomers: [],
        address: {
            name: '',
            attention: '',
            street: '',
            nr: '',
            addition: '',
            street2: '',
            postalCode: '',
            city: '',
            country: 'NL',
            isBusiness: true
        },
        phone: '',
        email: '',
        contacts: [],
        login: false,
        loginEmail: '',
        settings: {
            logo: '',
            color: '',
            useOwnSMTP: false,
            smtpHost: '',
            smtpPort: '',
            smtpUser: '',
            smtpPassword: '',
            openingHours: {
                monday: {
                    open: true
                },
                tuesday: {
                    open: true
                },
                wednesday: {
                    open: true
                },
                thursday: {
                    open: true
                },
                friday: {
                    open: true
                },
                saturday: {
                    open: false
                },
                sunday: {
                    open: false
                }
            },
            usePickupDaysForRetourOrder: false,
            orders: {
                defaultSigned: 'default',
                defaultStatedAddressOnly: 'default',
                nameRequired: 'default',
                gpsRequired: 'default',
                scanRequired: 'default',
                imageRequired: 'default',
                allowAddCollo: 'default',
                referenceRequired: false,
                newOrderInstructions: '',
                defaultOrderNotes: '',
                emailNotificationsReceiverOption: 'default',
                emailNotificationsReceiver: [],
                emailNotificationsCustomerOption: 'default',
                emailNotificationsCustomer: [],
                pickupNotPossibleReasons: ['default'],
                deliveryNotPossibleReasons: ['default'],
                pickupNotPossibleReasonsSpecifications: [],
                deliveryNotPossibleReasonsSpecifications: [],
                showNewOrderNotification: 'default',
                pharmacomOrderType: 'delivery',
                receptlocatieOrderType: 'delivery',
                voorzorgOrderType: 'delivery',
                voorzorgDeliveryDays: {
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: false,
                    sunday: false
                },
                defaultAddresses: 'default',
                defaultPickupOptions: 'default',
                defaultDeliveryOptions: 'default',
                allowedColliTypes: 'default',
                showColliBarcodeSpecification: 'default'
            },
            parcels: {
                'selectCarrier': false,
                'emailTT': '',
                'disabledCarriers': [],
                'cycloonNoParcelsOnFriday': '',
                'dhlEuroplusForBusinesAddress': '',
                'dhlQLSEuroplusForBusinesAddress': '',
                'dpdOnlineparcelSaturdayDelivery': '',
                'DHLnoParcelsForBusinessAddress': '',
                'DHL-MSGnoParcelsForBusinessAddress': '',
                'CycloonnoParcelsForBusinessAddress': '',
                'autoPickupOrder': false,
                'autoPickupPrice': '0,00',
                'coverAmount': '',
                'pickupDays': {
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: false,
                    sunday: false
                },
                'sameDayCuttoff': '00:00',
                'sameDayPickupStart': '16:00',
                'sameDayPickupEnd': '17:00',
                'nextDayCuttoff': '17:00',
                'nextDayPickupStart': '17:00',
                'nextDayPickupEnd': '18:00',
                'priceTable': '',
                'priceTableAbroad': '',
                'defaultOptions': [],
                'autoMailboxParcel': false,
                'useMailboxParcelWeight': false,
                'mailboxParcelWeight': 0,
                'useMailboxParcelValue': false,
                'mailboxParcelValue': 0,
                'defaultComment': '',
                'referenceNotOnLabel': false,
                'dontSendEmailToCarriers': false,
                'returnsVia': '',
                'senderAddress': {
                    name: '',
                    attention: '',
                    street: '',
                    nr: '',
                    addition: '',
                    street2: '',
                    postalCode: '',
                    city: '',
                    country: 'NL'
                },
                'emailNotifications': {
                    confirmedEmails: [
                        {
                            enabled: false,
                            language: 'NL',
                            country: 'NL',
                            senderName: '',
                            replyTo: '',
                            subject: defaultCustomerConfirmedEmailSubject,
                            body: defaultCustomerConfirmedEmailBody
                        },
                        {
                            enabled: false,
                            language: 'EN',
                            country: '',
                            default: true,
                            senderName: '',
                            replyTo: '',
                            subject: defaultCustomerConfirmedEmailSubject,
                            body: defaultCustomerConfirmedEmailBody
                        }
                    ],
                    shippedEmails: [
                        {
                            enabled: false,
                            language: 'NL',
                            country: 'NL',
                            senderName: '',
                            replyTo: '',
                            subject: defaultCustomerShippedEmailSubject,
                            body: defaultCustomerShippedEmailBody
                        },
                        {
                            enabled: false,
                            language: 'EN',
                            country: '',
                            default: true,
                            senderName: '',
                            replyTo: '',
                            subject: defaultCustomerShippedEmailSubject,
                            body: defaultCustomerShippedEmailBody
                        }
                    ],
                    cancelEmails: [
                        {
                            enabled: false,
                            language: 'NL',
                            country: 'NL',
                            senderName: '',
                            replyTo: '',
                            subject: defaultCustomerCancelEmailSubject,
                            body: defaultCustomerCancelEmailBody
                        },
                        {
                            enabled: false,
                            language: 'EN',
                            country: '',
                            default: true,
                            senderName: '',
                            replyTo: '',
                            subject: defaultCustomerCancelEmailSubject,
                            body: defaultCustomerCancelEmailBody
                        }
                    ],
                    retourConfirmationEmails: [
                        {
                            enabled: false,
                            language: 'NL',
                            country: 'NL',
                            senderName: '',
                            replyTo: '',
                            subject: defaultCustomerRetourConfirmationEmailSubject,
                            body: defaultCustomerRetourConfirmationEmailBody
                        },
                        {
                            enabled: false,
                            language: 'EN',
                            country: '',
                            default: true,
                            senderName: '',
                            replyTo: '',
                            subject: defaultCustomerRetourConfirmationEmailSubject,
                            body: defaultCustomerRetourConfirmationEmailBody
                        }
                    ]
                },

                'returnPortalActive': false,
                'returnPeriod': 14,
                'returnAddress': {
                    name: '',
                    attention: '',
                    street: '',
                    nr: '',
                    addition: '',
                    street2: '',
                    postalCode: '',
                    city: '',
                    country: 'NL'
                },
                'returnPolicyURL': '',
                'returnShippingCosts': {},
                'returnReasons': [],
                'returnRefundMethods': [],
                'mollie': {
                    active: false,
                    accessToken: '',
                    expireDate: '',
                    refreshToken: '',
                    websiteProfiles: []
                },
                'sizes': []
            },
            webshops: {
                lightspeed: {
                    active: false,
                    clusterId: '',
                    apiKey: '',
                    apiSecret: '',
                    language: '',
                    ignoreNotPayedOrders: false,
                    nrOfDays: 7,
                    updateStatus: '',
                    shipmentMethodTitle: 'Bezorg bij afhaalpunt',
                    shipmentMethodAllowedCountries: ['NL'],
                    shipmentMethodFreeFromOrderValue: '',
                    shipmentMethodPriceScales: [],
                    shippingMethods: []
                },
                shopify: {
                    active: false,
                    url: '',
                    apiKey: '',
                    nrOfDays: 7,
                    updateStatus: '',
                    ignoreNotPayedOrders: false
                },
                woocommerce: {
                    active: false,
                    url: '',
                    apiKey: '',
                    apiSecret: '',
                    nrOfDays: 7,
                    updateStatus: '',
                    statuses: [],
                    weightInGrams: false,
                    streetField: '',
                    nrField: '',
                    additionField: '',
                    shippingMethods: []
                },
                ccvshop: {
                    active: false,
                    statuses: ['2'],
                    includeProducts: 'always',
                    ignoreNotPayedOrders: true
                },
                bolcom: {
                    active: false
                },
                mijnwebwinkel: {
                    active: false
                },
                shoptrader: {
                    active: false
                },
                snelstart: {
                    active: false
                },
                exactOnline: {
                    active: false
                },
                magento: {
                    active: false,
                    url: '',
                    accessToken: '',
                    nrOfDays: 7,
                    updateStatus: '',
                    statuses: []
                },
                shopware: {
                    active: false
                },
                prestashop: {
                    active: false
                }
            },
            apiKey: '',
            webhookURL: '',
            parcelWebhookURL: '',
            webhookAuthHeader: '',
            mfaRequired: false
        },
        permissions: {
            parcels: true,
            ordersRead: false,
            orders: false
        },
        notes: ''
    }

    return JSON.parse(JSON.stringify(customer))
}
